/**
 * Plant Search — full search page with cascading filters + results grid.
 * Route: /plants
 *
 * Ports FAIM I's plant list/search behaviour:
 *  - Free-text term (plant name or plant code, case-insensitive).
 *  - Cascading geography filters: Area → Country → Business Line → Region.
 *    Region only lists options once both a country and a business line are chosen.
 *  - Operating Company filter (narrowed by country).
 *
 * State lives entirely in the URL search params so filters are shareable and
 * survive refresh. Filter-option lists are user-scoped on the server and refetch
 * as the parent selection changes.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { useQuery } from '@apollo/client/react';
import Button from '@mui/material/Button';
import { graphql } from '../../gql';
import { DataGrid } from '../../components/data-grid';
import { TextInput, SelectInput, PageHeader, LoadingOverlay } from '../../components/ui';
import type { SelectOption } from '../../components/ui';
import type { ColDef, RowClickedEvent } from 'ag-grid-community';

const PLANT_SEARCH_FILTER_OPTIONS_QUERY = graphql(`
  query PlantSearchFilterOptions(
    $areaId: Int
    $countryId: Int
    $businessLineId: Int
  ) {
    plantSearchFilterOptions(
      areaId: $areaId
      countryId: $countryId
      businessLineId: $businessLineId
    ) {
      areas { id name }
      countries { id name }
      businessLines { id name }
      regions { id name }
      operatingCompanies { id name }
    }
  }
`);

const SEARCH_PLANTS_QUERY = graphql(`
  query SearchPlants($filter: PlantSearchFilterInput) {
    searchPlants(filter: $filter) {
      id
      area
      country
      region
      businessLine
      plantName
      plantCode
      managementCode
      operatingCompany
    }
  }
`);

const DEBOUNCE_MS = 300;

interface PlantRow {
  id: number;
  area: string;
  country: string;
  region: string;
  businessLine: string;
  plantName: string;
  plantCode?: string | null;
  managementCode?: string | null;
  operatingCompany: string;
}

/** Parse a search param into a positive int id, or undefined when absent/invalid. */
function paramId(params: URLSearchParams, key: string): number | undefined {
  const raw = params.get(key);
  if (!raw) return undefined;
  const n = Number(raw);
  return Number.isInteger(n) && n > 0 ? n : undefined;
}

/** Build SelectInput options with a leading "All" entry. */
function withAll(label: string, options: ReadonlyArray<{ id: number; name: string }>): SelectOption[] {
  return [{ value: '', label }, ...options.map((o) => ({ value: o.id, label: o.name }))];
}

export function PlantSearchPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Derive the typed filter selections from the URL (single source of truth).
  const areaId = paramId(searchParams, 'area');
  const countryId = paramId(searchParams, 'country');
  const businessLineId = paramId(searchParams, 'bl');
  const regionId = paramId(searchParams, 'region');
  const operatingCompanyId = paramId(searchParams, 'oc');
  // Quick-search bar navigates here with `?input=`; treat it as the initial term.
  const term = searchParams.get('q') ?? searchParams.get('input') ?? '';

  // Local, debounced mirror of the free-text term so typing doesn't thrash the URL.
  const [termInput, setTermInput] = useState(term);

  useEffect(() => {
    const handle = setTimeout(() => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete('input');
          const trimmed = termInput.trim();
          if (trimmed) next.set('q', trimmed);
          else next.delete('q');
          return next;
        },
        { replace: true },
      );
    }, DEBOUNCE_MS);
    return () => clearTimeout(handle);
    // setSearchParams is stable; only re-run when the typed term changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [termInput]);

  // Cascading filter-option lists, scoped to the user's accessible plants and
  // narrowed by the current parent selections.
  const { data: optionsData, loading: optionsLoading } = useQuery(PLANT_SEARCH_FILTER_OPTIONS_QUERY, {
    variables: {
      areaId: areaId ?? null,
      countryId: countryId ?? null,
      businessLineId: businessLineId ?? null,
    },
  });

  const options = optionsData?.plantSearchFilterOptions;

  const filter = useMemo(
    () => ({
      areaId: areaId ?? null,
      countryId: countryId ?? null,
      businessLineId: businessLineId ?? null,
      regionId: regionId ?? null,
      operatingCompanyId: operatingCompanyId ?? null,
      searchTerm: term.trim() || null,
    }),
    [areaId, countryId, businessLineId, regionId, operatingCompanyId, term],
  );

  const { data: searchData, loading: searchLoading, error } = useQuery(SEARCH_PLANTS_QUERY, {
    variables: { filter },
  });

  const plants = (searchData?.searchPlants ?? []) as PlantRow[];

  /**
   * Update one filter param and clear the descendants that no longer apply,
   * so a stale child selection can never survive a parent change.
   */
  const updateFilter = useCallback(
    (key: string, value: string, clearChildren: string[]) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        if (value) next.set(key, value);
        else next.delete(key);
        clearChildren.forEach((child) => next.delete(child));
        return next;
      });
    },
    [setSearchParams],
  );

  const handleClear = useCallback(() => {
    setTermInput('');
    setSearchParams({});
  }, [setSearchParams]);

  const columnDefs = useMemo<ColDef<PlantRow>[]>(
    () => [
      { field: 'area', headerName: 'Area', flex: 1 },
      { field: 'country', headerName: 'Country', flex: 1 },
      { field: 'region', headerName: 'Region', flex: 1 },
      { field: 'businessLine', headerName: 'Business Line', flex: 1 },
      { field: 'plantName', headerName: 'Plant Name', flex: 1 },
      { field: 'plantCode', headerName: 'Plant Code', flex: 1 },
      { field: 'managementCode', headerName: 'Management ID', flex: 1 },
      { field: 'operatingCompany', headerName: 'Operating Company', flex: 1 },
    ],
    [],
  );

  const onRowClicked = useCallback(
    (event: RowClickedEvent<PlantRow>) => {
      if (event.data) navigate(`/plants/${event.data.id}`);
    },
    [navigate],
  );

  return (
    <>
      <LoadingOverlay visible={searchLoading || optionsLoading} />

      <main className="max-w-[1440px] mx-auto px-6 py-8">
        <PageHeader title="Plant Search" />

        {/* Filter bar */}
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <TextInput
            search
            value={termInput}
            onChange={(e) => setTermInput(e.target.value)}
            placeholder="Search by plant name or code..."
          />
          <SelectInput
            label="Area"
            fullWidth={false}
            sx={{ minWidth: 160 }}
            value={areaId ? String(areaId) : ''}
            onChange={(e) => updateFilter('area', String(e.target.value), ['country', 'bl', 'region', 'oc'])}
            options={withAll('All Areas', options?.areas ?? [])}
          />
          <SelectInput
            label="Country"
            fullWidth={false}
            sx={{ minWidth: 160 }}
            value={countryId ? String(countryId) : ''}
            onChange={(e) => updateFilter('country', String(e.target.value), ['bl', 'region', 'oc'])}
            options={withAll('All Countries', options?.countries ?? [])}
          />
          <SelectInput
            label="Business Line"
            fullWidth={false}
            sx={{ minWidth: 160 }}
            value={businessLineId ? String(businessLineId) : ''}
            onChange={(e) => updateFilter('bl', String(e.target.value), ['region'])}
            options={withAll('All Business Lines', options?.businessLines ?? [])}
          />
          <SelectInput
            label="Region"
            fullWidth={false}
            sx={{ minWidth: 160 }}
            value={regionId ? String(regionId) : ''}
            onChange={(e) => updateFilter('region', String(e.target.value), [])}
            options={withAll('All Regions', options?.regions ?? [])}
          />
          <SelectInput
            label="Operating Company"
            fullWidth={false}
            sx={{ minWidth: 160 }}
            value={operatingCompanyId ? String(operatingCompanyId) : ''}
            onChange={(e) => updateFilter('oc', String(e.target.value), [])}
            options={withAll('All Operating Companies', options?.operatingCompanies ?? [])}
          />
          <Button variant="outlined" onClick={handleClear}>
            Clear
          </Button>
        </div>

        {/* Error */}
        {error && (
          <div className="mb-4 p-3 bg-negative-faded-20 border border-negative rounded text-sm text-negative">
            {error.message}
          </div>
        )}

        {/* Results grid */}
        {plants.length > 0 && (
          <DataGrid<PlantRow>
            rowData={plants}
            columnDefs={columnDefs}
            onRowClicked={onRowClicked}
            height="75vh"
          />
        )}

        {/* Empty state */}
        {searchData && plants.length === 0 && (
          <div className="text-center py-12 text-foreground-faded">
            No plants match the current filters.
          </div>
        )}
      </main>
    </>
  );
}
